/**
 * Measurement Tab — min/max dimensions, clearances
 */
import { useState, useCallback } from 'react';
import { Save, Loader2, Ruler } from 'lucide-react';
import { PIMMeasurementParams } from '@/api';

interface MeasurementTabProps {
  params: PIMMeasurementParams;
  onSave: (params: PIMMeasurementParams) => void;
  isSaving: boolean;
}

interface DimensionField {
  key: keyof PIMMeasurementParams;
  label: string;
  unit: string;
  min?: number;
  max?: number;
  step?: number;
}

const FIELDS: DimensionField[] = [
  { key: 'min_width_cm', label: 'Мин. ширина', unit: 'см', min: 0, max: 1000, step: 1 },
  { key: 'max_width_cm', label: 'Макс. ширина', unit: 'см', min: 0, max: 1000, step: 1 },
  { key: 'min_drop_cm', label: 'Мин. высота', unit: 'см', min: 0, max: 500, step: 1 },
  { key: 'max_drop_cm', label: 'Макс. высота', unit: 'см', min: 0, max: 500, step: 1 },
  { key: 'side_clearance_cm', label: 'Боковой зазор', unit: 'см', min: 0, max: 50, step: 0.5 },
];

export function MeasurementTab({ params, onSave, isSaving }: MeasurementTabProps) {
  const [values, setValues] = useState<PIMMeasurementParams>(params);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = useCallback((key: keyof PIMMeasurementParams, value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    setValues((prev) => ({ ...prev, [key]: numValue }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(() => {
    onSave(values);
    setHasChanges(false);
  }, [values, onSave]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,179,237,0.15)' }}>
            <Ruler size={20} style={{ color: '#63B3ED' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
              Параметры замера
            </h3>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>
              Укажите минимальные и максимальные размеры для конфигуратора
            </p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(246,173,85,0.12)', color: '#F6AD55' }}>
            <span>Изменения не сохранены</span>
          </div>
        )}
      </div>

      {/* Fields Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map((field) => (
          <div key={field.key} className="space-y-2">
            <label className="text-xs font-medium flex items-center justify-between" style={{ color: 'var(--text-2)' }}>
              {field.label}
              <span className="text-[10px] font-normal" style={{ color: 'var(--text-3)' }}>{field.unit}</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={values[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                min={field.min}
                max={field.max}
                step={field.step}
                className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#63B3ED]/50 focus:outline-none transition-colors"
                style={{ color: 'var(--text-1)' }}
                placeholder="—"
              />
              {values[field.key] !== undefined && values[field.key] !== null && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>
                  {field.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Card */}
      <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)' }}>
        <h4 className="text-xs font-medium mb-2" style={{ color: 'var(--text-2)' }}>
          Как это используется в конфигураторе
        </h4>
        <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-3)' }}>
          <li>• Мин/макс ширина — ограничивает ввод пользователя</li>
          <li>• Мин/макс высота — проверяет возможность изготовления</li>
          <li>• Боковой зазор — добавляется к расчётной ширине</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-40"
          style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED' }}>
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {isSaving ? 'Сохранение...' : 'Сохранить параметры'}
        </button>
      </div>
    </div>
  );
}
